import React, { useCallback, useRef, useState } from 'react';

interface UploadCardProps {
  title: string;
  hint?: string;
  image?: string;
  isExample?: boolean;
  uploading?: boolean;
  disabled?: boolean;
  onSelect: (file: File) => void;
  onRemove: () => void;
  className?: string;
}

const UploadCard: React.FC<UploadCardProps> = ({
  title,
  hint,
  image,
  isExample = false,
  uploading = false,
  disabled = false,
  onSelect,
  onRemove,
  className = '',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      const file = files?.[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        setLocalError('Please choose an image file.');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setLocalError('Image must be 10MB or smaller.');
        return;
      }
      setLocalError(null);
      onSelect(file);
    },
    [onSelect]
  );

  const openPicker = () => {
    if (disabled || uploading) return;
    inputRef.current?.click();
  };

  return (
    <div
      className={`relative ${className}`}
      onDrop={(event) => {
        event.preventDefault();
        setIsDragging(false);
        if (disabled) return;
        handleFiles(event.dataTransfer.files);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
    >
      {image ? (
        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
          <div className="relative">
            <span className="absolute left-4 top-4 z-10 rounded-full bg-white/90 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-600 shadow-sm backdrop-blur">
              {title}
            </span>
            <button
              type="button"
              onClick={onRemove}
              className="absolute right-4 top-4 z-10 rounded-full bg-white/90 p-2 text-gray-500 shadow-sm backdrop-blur transition-colors hover:bg-red-50 hover:text-red-500"
              aria-label="Remove image"
              title="Remove"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="flex h-[360px] items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-6">
              <img
                src={image}
                alt={isExample ? 'Example garment' : 'Uploaded garment'}
                className="max-h-full max-w-full rounded-2xl object-contain drop-shadow-xl"
              />
            </div>
          </div>
          <div className="flex items-center justify-between border-t border-gray-100 bg-white px-4 py-3">
            <p className="text-xs text-gray-500">
              {isExample ? 'Example garment' : 'Uploaded successfully'}
            </p>
            <button
              type="button"
              onClick={openPicker}
              disabled={disabled || uploading}
              className="rounded-lg border border-gray-200 px-3.5 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:border-secondary-400 hover:bg-secondary-50 disabled:opacity-50"
            >
              Replace image
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          disabled={disabled || uploading}
          className={`group flex h-[360px] w-full flex-col items-center justify-center rounded-3xl border-2 border-dashed px-6 text-center transition-all duration-300 ${
            isDragging
              ? 'border-secondary-500 bg-secondary-50'
              : 'border-gray-300 bg-gradient-to-b from-white to-gray-50 hover:border-secondary-400'
          } ${disabled || uploading ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
        >
          {uploading ? (
            <>
              <span className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-4 border-secondary-500 border-t-transparent" />
              <span className="text-sm font-semibold text-gray-700">Uploading...</span>
            </>
          ) : (
            <>
              <span className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-secondary-100 text-secondary-600 transition-transform duration-300 group-hover:scale-105">
                <i className="fas fa-cloud-upload-alt text-3xl" />
              </span>
              <span className="text-base font-bold text-gray-900">{title}</span>
              <span className="mt-1 text-sm text-gray-500">{hint || 'Drag & drop, or click to browse'}</span>
              <span className="mt-5 rounded-xl bg-secondary-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors group-hover:bg-secondary-700">
                Choose image
              </span>
              <span className="mt-4 text-[11px] font-medium uppercase tracking-wider text-gray-400">
                JPG · PNG · up to 10MB
              </span>
            </>
          )}
        </button>
      )}

      {localError && (
        <p className="mt-3 text-center text-xs font-medium text-red-500">{localError}</p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) => {
          handleFiles(event.target.files);
          event.target.value = '';
        }}
      />
    </div>
  );
};

export default UploadCard;
